import {
  Button,
  Icon,
  ReactionsButton,
  Toast,
} from '@socialchorus/shared-ui-components';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trackCommentEvent } from '../../../../../models/comments/analytics';
import { Comment } from '../../../../../models/comments/types';
import {
  likeComment as likeCommentService,
  unlikeComment as unlikeCommentService,
} from '../../../../../services/comment';
import { usePatronSelector } from '../../../../../common/use-patron-selector';
import { programSelectors } from '../../../../../models/program';
import { useMutation } from '@tanstack/react-query';
import { advocateSelectors } from '../../../../../models/advocate';

type CommentActionLikeProps = {
  comment: Comment;
};

function CommentActionLikeOnly({ comment }: CommentActionLikeProps) {
  const { t } = useTranslation();

  const [liked, setLiked] = useState(comment.isLiked);
  const [canLike, setCanLike] = useState(comment.canLike);
  const [likeCount, setLikeCount] = useState(comment.likeCount);

  const likeComment = () => {
    if (!canLike) return;

    trackCommentEvent('like', comment, !!comment.replyToId);

    likeCommentService(comment.contentId, comment.id);

    setLiked(true);
    setCanLike(false);
    setLikeCount(likeCount + 1);
  };

  const unlikeComment = () => {
    trackCommentEvent('unlike', comment, !!comment.replyToId);

    unlikeCommentService(comment.contentId, comment.id);

    setLiked(false);
    setCanLike(true);
    setLikeCount(Math.max(0, likeCount - 1));
  };

  const emptyLikes = likeCount == 0;

  return (
    <>
      <Button
        size="compact"
        variant="text"
        selected={liked}
        label={`${emptyLikes ? '' : likeCount}`}
        leftIcon={<Icon size={20}>thumb_up</Icon>}
        aria-label={
          canLike
            ? t(liked ? 'content.like_remove' : 'content.like_one')
            : undefined
        }
        onClick={liked ? unlikeComment : likeComment}
      />
      <div className="divider" />
    </>
  );
}

function CommentActionExpandedReactions({ comment }: CommentActionLikeProps) {
  const { t } = useTranslation();
  const currentUserId = usePatronSelector((state) =>
    advocateSelectors.getAdvocateId(state)
  );

  const selectedReaction = useMemo(
    () =>
      comment.expandedReactions &&
      comment.expandedReactions.find((reaction) => reaction.reacted)?.id,
    [comment.expandedReactions]
  );

  const availableReactions = useMemo(
    () =>
      comment.expandedReactions?.map((reaction) => ({
        id: reaction.id,
        label: reaction.title,
        emoji: reaction.emoji,
        default: reaction.default,
      })),
    [comment.expandedReactions]
  );

  const totalReactionsCount = useMemo(
    () =>
      comment.expandedReactions?.reduce(
        (acc, reaction) => acc + reaction.count,
        0
      ) || 0,
    [comment.expandedReactions]
  );

  const top3ReactionsTypes = useMemo(
    () =>
      comment.expandedReactions
        ?.filter((r) => r.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 3)
        .map((reaction) => reaction.emoji) || [],
    [comment.expandedReactions]
  );

  const { mutate: setNewReaction, isError } = useMutation({
    mutationFn: (reactionId: string | null) => {
      if (!reactionId) {
        return unlikeCommentService(comment.contentId, comment.id);
      }
      return likeCommentService(comment.contentId, comment.id, reactionId);
    },
    onMutate: (reactionId) => {
      if (!comment.expandedReactions) return;
      const oldReactions = [...comment.expandedReactions];

      comment.expandedReactions = comment.expandedReactions.map((reaction) => {
        if (reaction.id === reactionId) {
          return {
            ...reaction,
            reacted: true,
            count: reaction.count + 1,
          };
        } else if (reaction.reacted) {
          return {
            ...reaction,
            reacted: false,
            count: Math.max(0, reaction.count - 1),
          };
        }
        return reaction;
      });

      return oldReactions; //will be passed to onError
    },
    onSuccess: (data, reactionId) => {
      if (!reactionId) {
        trackCommentEvent('unlike', comment, !!comment.replyToId, {
          reactionId,
        });
      } else {
        if (!selectedReaction) {
          trackCommentEvent('like', comment, !!comment.replyToId, {
            reactionId,
          });
        } else {
          trackCommentEvent('like_update', comment, !!comment.replyToId, {
            reactionId,
          });
        }
      }
    },
    onError: (error, reactionId, oldReactions) => {
      console.error('Error in comment reaction mutation:', error);

      if (oldReactions) {
        comment.expandedReactions = oldReactions;
      }
    },
  });

  const handleSetNewReaction = (reactionId: string | null) =>
    reactionId !== selectedReaction && setNewReaction(reactionId);

  const userId = comment.userId ?? +comment.author.id;
  const showButton =
    userId !== currentUserId &&
    availableReactions &&
    availableReactions?.length > 0;
  const showReactionStats = totalReactionsCount > 0;

  if (!showButton && !showReactionStats) {
    return null;
  }

  return (
    <>
      <div className="reaction-button-container">
        {showButton ? (
          <ReactionsButton
            availableReactions={availableReactions || []}
            selectedReactionId={selectedReaction || null}
            onReactionSelect={handleSetNewReaction}
            size="compact"
          />
        ) : null}
        {showReactionStats ? (
          <>
            <div className="reaction-types-container">
              {top3ReactionsTypes.length > 0 &&
                top3ReactionsTypes.map((emoji) => (
                  <span key={emoji}>{emoji}</span>
                ))}
            </div>
            <div>{t('compact_number', { value: totalReactionsCount })}</div>
          </>
        ) : null}
      </div>
      <div className="divider" />
      {isError && (
        <Toast
          title={t('comments.errors.reaction_failed')}
          variant="error"
          defaultOpen
        />
      )}
    </>
  );
}

export function CommentActionLike({ comment }: CommentActionLikeProps) {
  const expandedReactionTypesEnabled = usePatronSelector((state) =>
    programSelectors.getExpandedReactionTypesEnabled(state)
  );
  if (expandedReactionTypesEnabled) {
    return <CommentActionExpandedReactions comment={comment} />;
  } else {
    return <CommentActionLikeOnly comment={comment} />;
  }
}
